import { Sun, Droplets, Ruler, Star, AlertTriangle } from 'lucide-react';
import { usePlant } from '../../../context/PlantContext';
import { useGardenSettings } from '../../../context/GardenSettingsContext';
import { getPlantImageUrl } from '../../../utils/plantImageUtils';
import HardinessWarningBadge from './HardinessWarningBadge';
import {
  SUNLIGHT_LABELS,
  WATER_REQUIREMENT_LABELS,
  translateEnum,
  translateEnumList,
} from '../../../utils/plantEnumLabels';

/**
 * PlantCard - Catalog plant card (420×180px)
 *
 * Large horizontal card for plant catalog with image, properties,
 * favorite toggle, and action buttons.
 *
 * @param {Object} props - Component props
 * @param {Object} props.plant - Plant object with full properties
 * @param {Function} props.onPlaceClick - Callback when "Platzieren" clicked
 * @param {Function} props.onDetailsClick - Callback when "Details" clicked
 */
const PlantCard = ({ plant, onPlaceClick, onDetailsClick }) => {
  const { favorites, addToFavorites, removeFromFavorites, enterPlacementMode } = usePlant();
  const { settings } = useGardenSettings();

  if (!plant) return null;

  const {
    id,
    name, // API list view uses 'name'
    german_name, // Detail view might use 'german_name'
    scientific_name,
    properties = {}, // Detail view nests properties here
    care_instructions = {},
    // List view has flat properties
    sunlight: flatSunlight,
    water_requirement: flatWaterRequirement,
    min_height_cm: flatHeightMin,
    max_height_cm: flatHeightMax,
    toxic_to_pets: flatToxicToPets,
    toxic_to_children: flatToxicToChildren
  } = plant;

  // Support both flat (list API) and nested (detail API) property formats
  const sunlight = flatSunlight || properties.sunlight;
  const water_requirement = flatWaterRequirement || properties.water_requirement;
  const height_min = flatHeightMin !== undefined ? flatHeightMin : properties.min_height_cm;
  const height_max = flatHeightMax !== undefined ? flatHeightMax : properties.max_height_cm;
  const toxic_to_pets = flatToxicToPets !== undefined ? flatToxicToPets : properties.toxic_to_pets;
  const toxic_to_children = flatToxicToChildren !== undefined ? flatToxicToChildren : properties.toxic_to_children;

  // Prefer german_name if available, fallback to name
  const displayName = german_name || name;
  const imageName = name || german_name; // DB 'name' matches image filename

  const isFavorite = favorites.includes(id);
  const isToxic = toxic_to_pets || toxic_to_children;
  const isLawn = properties?.growth_type === 'lawn';

  const plantZone = care_instructions?.min_hardiness_zone || properties?.min_hardiness_zone;
  const gardenZone = settings?.hardinessZone;

  // Format sunlight/water display (zentrale Labels, #751)
  const getSunlightDisplay = (value) =>
    translateEnumList(SUNLIGHT_LABELS, value, 'sunlight') || 'Unbekannt';

  const getWaterDisplay = (value) =>
    translateEnum(WATER_REQUIREMENT_LABELS, value, 'water_requirement') || 'Unbekannt';

  // Format height display
  const getHeightDisplay = () => {
    if (height_min && height_max) {
      return `${height_min} - ${height_max} cm`;
    } else if (height_min) {
      return `ab ${height_min} cm`;
    } else if (height_max) {
      return `bis ${height_max} cm`;
    }
    return 'Unbekannt';
  };

  // Handle favorite toggle
  const handleFavoriteToggle = (e) => {
    e.stopPropagation(); // Prevent card click
    if (isFavorite) {
      removeFromFavorites(id);
    } else {
      addToFavorites(id);
    }
  };

  // Handle place click
  const handlePlaceClick = (e) => {
    e.stopPropagation();

    // Enter placement mode (triggers PlantPlacementManager)
    enterPlacementMode(plant);

    // Also call parent callback if provided
    if (onPlaceClick) {
      onPlaceClick(plant);
    }
  };

  // Handle details click
  const handleDetailsClick = (e) => {
    e.stopPropagation();
    if (onDetailsClick) {
      onDetailsClick(plant);
    }
  };

  return (
    <div className="flex items-stretch gap-4 w-[420px] h-[200px] bg-white border border-gray-200 rounded-lg p-4 hover:border-emerald-400 transition-colors duration-200">
      {/* Plant Image - 150×150px */}
      <div className="flex-shrink-0 self-center w-[150px] h-[150px] bg-gray-100 rounded-lg overflow-hidden">
        {imageName ? (
          <img
            src={getPlantImageUrl(imageName, 'catalog')}
            alt={displayName || imageName}
            className="w-full h-full object-cover"
            loading="lazy"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">
            Kein Bild
          </div>
        )}
      </div>

      {/* Plant Info - Flex grows to fill space */}
      <div className="flex-1 flex flex-col justify-between min-w-0">
        {/* Header with Name and Star */}
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-gray-900 truncate">
              {displayName || 'Unbekannte Pflanze'}
            </h3>
            {scientific_name && (
              <p className="text-xs text-gray-500 italic truncate">
                {scientific_name}
              </p>
            )}
          </div>

          {/* Star Button */}
          <button
            onClick={handleFavoriteToggle}
            aria-label={isFavorite ? 'Aus Favoriten entfernen' : 'Zu Favoriten hinzufügen'}
            className="flex-shrink-0 p-1 hover:bg-gray-100 rounded transition-colors duration-200"
          >
            <Star
              className={`w-5 h-5 ${
                isFavorite
                  ? 'fill-yellow-500 text-yellow-500'
                  : 'text-gray-400'
              }`}
            />
          </button>
        </div>

        {/* Properties Grid - 3 rows with icons */}
        <div className="flex flex-col gap-1 mb-2">
          {/* Sunlight */}
          <div className="flex items-center gap-2 text-xs text-gray-700">
            <Sun className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <span>{getSunlightDisplay(sunlight)}</span>
          </div>

          {/* Water Requirement */}
          <div className="flex items-center gap-2 text-xs text-gray-700">
            <Droplets className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <span>{getWaterDisplay(water_requirement)}</span>
          </div>

          {/* Height - Only show if height data available */}
          {(height_min || height_max) && (
            <div className="flex items-center gap-2 text-xs text-gray-700">
              <Ruler className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <span>{getHeightDisplay()}</span>
            </div>
          )}
        </div>

        {/* Warning Badges Row — toxic + hardiness in one line */}
        {(isToxic || (plantZone && gardenZone)) && (
          <div className="flex items-center gap-1.5 flex-wrap mb-2">
            {isToxic && (
              <div className="inline-flex items-center gap-1 px-2 py-1 bg-red-50 border border-red-200 rounded text-xs text-red-900">
                <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                <span>Giftig</span>
              </div>
            )}
            <HardinessWarningBadge plantZone={plantZone} gardenZone={gardenZone} variant="compact" />
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          {!isLawn && (
            <button
              onClick={handlePlaceClick}
              className="flex-1 px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-medium rounded transition-colors duration-200"
            >
              Platzieren
            </button>
          )}
          <button
            onClick={handleDetailsClick}
            className="flex-1 px-3 py-1.5 bg-white hover:bg-gray-50 border border-gray-300 text-gray-700 text-xs font-medium rounded transition-colors duration-200"
          >
            Details
          </button>
        </div>
      </div>
    </div>
  );
};

export default PlantCard;
